import { useId, useMemo, useState } from "react";
import { quadraticCost } from "@voting/quadratic";
import {
	RankedBallot,
	defaultRankOrder,
} from "./voting/RankedBallot";
import {
	QuadraticBallot,
	emptyQuadraticAlloc,
} from "./voting/QuadraticBallot";

export type VotingMechanicsDemoProps = {
	/** Home teaser: two options. Full explainer uses three. */
	compact?: boolean;
	className?: string;
};

type DemoOpt = { id: number; content: string };

const COMPACT_OPTIONS: DemoOpt[] = [
	{ id: 1, content: "Tuesday 2pm" },
	{ id: 2, content: "Thursday 10am" },
];

const FULL_OPTIONS: DemoOpt[] = [
	{ id: 1, content: "Ship the quiet fix" },
	{ id: 2, content: "Schedule a workshop" },
	{ id: 3, content: "Park it for next quarter" },
];

type DemoTab = "ranked" | "weighted";

/**
 * Interactive ranked / weighted explainer (Execution PRD §2.8).
 * Reuses ballot reorder + quadratic budget controls — does not fork drag math.
 */
export function VotingMechanicsDemo({ compact = false, className = "" }: VotingMechanicsDemoProps) {
	const tabBaseId = useId();
	const options = compact ? COMPACT_OPTIONS : FULL_OPTIONS;
	const [tab, setTab] = useState<DemoTab>("ranked");
	const [rankOrder, setRankOrder] = useState(() => defaultRankOrder(options));
	const [quadAlloc, setQuadAlloc] = useState(() => emptyQuadraticAlloc(options));
	const [gestureOk, setGestureOk] = useState(false);

	const spent = useMemo(() => {
		const asStrings: Record<string, number> = {};
		for (const [k, v] of Object.entries(quadAlloc)) asStrings[String(k)] = v;
		return quadraticCost(asStrings);
	}, [quadAlloc]);
	const budgetSpent = spent >= 100;

	const rankedPanelId = `${tabBaseId}-ranked`;
	const weightedPanelId = `${tabBaseId}-weighted`;

	return (
		<div
			className={`space-y-md rounded-xl border border-wi-border bg-wi-surface/60 p-4 sm:p-5 ${className}`}
			data-wi-voting-mechanics-demo={compact ? "compact" : "full"}
		>
			<div className="space-y-xs">
				<p className="font-medium text-body-sm text-wi-text">Try the two voting styles</p>
				<p className="text-body-sm text-wi-text-secondary leading-relaxed">
					{compact
						? "Reorder for ranked choice, or spend a shared budget for weighted picks — no account needed."
						: "Play with the same controls voters use. Ranked choice turns order into points; weighted picks make extra intensity cost more."}
				</p>
			</div>

			<div role="tablist" aria-label="Voting style demo" className="flex flex-wrap gap-2">
				<button
					type="button"
					role="tab"
					id={`${tabBaseId}-tab-ranked`}
					aria-selected={tab === "ranked"}
					aria-controls={rankedPanelId}
					tabIndex={tab === "ranked" ? 0 : -1}
					className={[
						"min-h-11 rounded-lg border px-3 py-2 text-left font-medium text-sm transition-colors duration-150",
						"motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wi-gold/45",
						tab === "ranked"
							? "border-wi-gold/50 bg-wi-gold/10 text-wi-text"
							: "border-wi-border text-wi-text-secondary hover:border-wi-gold/30",
					].join(" ")}
					onClick={() => setTab("ranked")}
				>
					Ranked choice
				</button>
				<button
					type="button"
					role="tab"
					id={`${tabBaseId}-tab-weighted`}
					aria-selected={tab === "weighted"}
					aria-controls={weightedPanelId}
					tabIndex={tab === "weighted" ? 0 : -1}
					className={[
						"min-h-11 rounded-lg border px-3 py-2 text-left font-medium text-sm transition-colors duration-150",
						"motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wi-gold/45",
						tab === "weighted"
							? "border-wi-gold/50 bg-wi-gold/10 text-wi-text"
							: "border-wi-border text-wi-text-secondary hover:border-wi-gold/30",
					].join(" ")}
					onClick={() => setTab("weighted")}
				>
					Weighted picks (quadratic voting)
				</button>
			</div>

			{tab === "ranked" ? (
				<div
					role="tabpanel"
					id={rankedPanelId}
					aria-labelledby={`${tabBaseId}-tab-ranked`}
					className="space-y-sm"
				>
					<p className="text-caption text-wi-text-muted leading-relaxed">
						Drag or use the arrows — higher ranks earn more Borda points when the poll ends.
					</p>
					<RankedBallot
						options={options}
						order={rankOrder.length === options.length ? rankOrder : defaultRankOrder(options)}
						onOrderChange={setRankOrder}
						disabled={false}
						ballotResetKey={0}
						onGestureEligibleChange={setGestureOk}
					/>
					{gestureOk ? (
						<p className="text-caption text-wi-text-muted">Order updated — this is how ranked choice feels in a live poll.</p>
					) : null}
				</div>
			) : (
				<div
					role="tabpanel"
					id={weightedPanelId}
					aria-labelledby={`${tabBaseId}-tab-weighted`}
					className="space-y-sm"
				>
					<p className="text-caption text-wi-text-muted leading-relaxed">
						Shared 100-point budget. Cost for each option is allocation² — push one idea hard and the budget drains fast.
					</p>
					<QuadraticBallot
						options={options}
						alloc={quadAlloc}
						onAllocChange={setQuadAlloc}
						disabled={false}
					/>
					{budgetSpent ? (
						<p className="text-caption text-wi-signal leading-relaxed" role="status">
							Nice — that&apos;s some serious Worthy Points math. The budget is fully spent.
						</p>
					) : (
						<p className="text-caption text-wi-text-muted">Spent {spent} of 100 budget points.</p>
					)}
				</div>
			)}
		</div>
	);
}
